import React, { StrictMode, useEffect } from 'react';
import { Switch, Route, useLocation } from 'react-router-dom';
import { TELEMETRY } from './common/settings';
import { useDispatch, useSelector } from 'react-redux';
import useTelemetry from './common/hooks/useTelemetry';
import { history } from './store';
import { scrollToSwoosh, handleFocus } from './common/functions';

import './common/styles/index.scss';
import { HeroLayout, MainLayout } from './scenes/layouts';
import { PageContentHeader } from './common/components';
import {
  formStartedSelector,
  formCompletedSelector,
  representativeSelector,
  servicesRequiredSelector,
} from './store/app/appSelectors';
import useAuth from './common/hooks/useAuth';
import { resetApp } from './store/app/appActions';
import { PSR_REGISTER, PSR_REVIEW } from './common/settings/urls';
import { tokenSelector } from './store/auth/selectors';
import { isEditingSelector } from './store/app/appSelectors';
import RenderScenes from './common/components/RenderScenes/RenderScenes';
import AppBreadcrumbs from './common/components/AppBreadcrumbs/AppBreadcrumbs';
import { resetCustomer } from './store/customer/customerActions';
import { resetNomineeDetails } from './store/nominee/nomineeActions';
import { getCurrentStepName } from './common/functions/helpers';
import { usePageTitle } from 'yw-ui.v2';

function App() {
  useTelemetry(TELEMETRY.INSTRUMENTATION_KEY, TELEMETRY.IS_ENABLED, history, TELEMETRY.REPLACE_TITLE);

  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const token = useSelector(tokenSelector);
  const formStarted = useSelector(formStartedSelector);
  const formCompleted = useSelector(formCompletedSelector);
  const isEditing = useSelector(isEditingSelector);
  const representative = useSelector(representativeSelector);
  const servicesRequired = useSelector(servicesRequiredSelector);

  const { isLoggedIn } = useAuth();

  const pageName = getCurrentStepName(pathname, representative);
  usePageTitle({
    title: pageName,
    defaultTitle: 'Who are you applying for',
    appName: `Priority Services ${pathname.split('/')[1]}`,
  });

  useEffect(() => {
    // Handle redirecting user to app start page if pathname does not match the journey
    const pathnameSplit = pathname.split('/');
    const updatedPathname = `/${pathnameSplit[1]}`;
    if (!formStarted) {
      if (updatedPathname === PSR_REGISTER) {
        history.push(PSR_REGISTER);
      } else if (updatedPathname === PSR_REVIEW) {
        history.push(PSR_REVIEW);
      } else {
        history.push(PSR_REGISTER);
      }
    }
  }, [pathname, formStarted]);

  const handleBack = () => {
    history.goBack();
    scrollToSwoosh();
    handleFocus('#page-heading_heading');
  };

  const handleResetForm = () => {
    dispatch(resetApp());
    dispatch(resetCustomer());
    dispatch(resetNomineeDetails());
  };

  const onRenderScene = (props, journey) => {
    return (
      <RenderScenes
        {...props}
        journey={journey}
        isLoggedIn={isLoggedIn}
        token={token}
        pathname={pathname}
        isEditing={isEditing}
        formStarted={formStarted}
        formCompleted={formCompleted}
        representative={representative}
        servicesRequired={servicesRequired}
        history={history}
        handleBack={handleBack}
        handleResetForm={handleResetForm}
      />
    );
  };

  return (
    <StrictMode>
      <MainLayout>
        <Switch>
          <HeroLayout heroCollapse={true}>
            <AppBreadcrumbs pathname={pathname} representative={representative} />
            <PageContentHeader pathname={pathname} representative={representative} />
            <Route path={PSR_REGISTER} render={(props) => onRenderScene(props, PSR_REGISTER)} />
            <Route path={PSR_REVIEW} render={(props) => onRenderScene(props, PSR_REVIEW)} />
            <Route path="/" render={(props) => onRenderScene(props, PSR_REGISTER)} exact />
          </HeroLayout>
        </Switch>
      </MainLayout>
    </StrictMode>
  );
}

export default App;
